import React, { useEffect, useState } from "react";
import { api, formatApiError, agencyLogoUrl } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { TransmissionModal, heroUrl } from "@/components/TransmissionModal";
import { Radio, Plus, ArrowLeft, ImagePlus, Loader2, Eye, Trash2, Send, Link2, Search, Check } from "lucide-react";

const GROUPS = [
  { key: "admin", label: "Admin" },
  { key: "operations", label: "Operations" },
  { key: "manager", label: "CMO" },
  { key: "account_manager", label: "Account Manager" },
  { key: "crew", label: "Crew" },
];
const MODULES = [
  ["inbox", "Inbox"], ["home", "Clients"], ["projects", "Projects"], ["tasks", "Tasks"],
  ["scheduler", "Scheduler"], ["workload", "Workload"], ["timesheet", "Timesheet"], ["insights", "Insights"],
  ["internal", "Internal Projects"], ["knowledge", "Knowledge Base"], ["forms", "Forms"], ["topo", "AI Agents"], ["team", "Team"],
];
const QUILL_MODULES = { toolbar: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]] };

function fmt(iso) { try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }

export default function ConsoleTransmissions({ agency }) {
  const [rows, setRows] = useState(null);
  const [mode, setMode] = useState("list"); // list | edit
  const [viewing, setViewing] = useState(null);
  const logoUrl = agencyLogoUrl(agency);

  const load = async () => { try { const { data } = await api.get("/console/transmissions"); setRows(data.transmissions || []); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (!(await confirmDelete({ message: "Delete this transmission? Recipients' copies will be removed." }))) return; try { await api.delete(`/console/transmissions/${id}`); toast.success("Transmission deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <main className="px-6 md:px-10 py-10 max-w-5xl mx-auto" data-testid="console-transmissions-page">
      {mode === "edit" ? (
        <TransmissionEditor agency={agency} logoUrl={logoUrl} onBack={() => setMode("list")} onSent={() => { setMode("list"); load(); }} />
      ) : (
        <>
          <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] flex items-center gap-2.5"><Radio className="h-7 w-7 text-[#723FFF]" /> Transmissions</h1>
              <p className="mt-2 text-sm text-[#9FA1A4]">Send in-app broadcasts to your agency team. Recipients see a centre-screen message on their next visit.</p>
            </div>
            <Button data-testid="transmission-new" onClick={() => setMode("edit")} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New transmission</Button>
          </div>

          {rows === null ? (
            <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-14 text-center" data-testid="transmissions-empty">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-4"><Radio className="h-6 w-6" /></div>
              <p className="text-sm text-[#9FA1A4]">No transmissions sent yet. Create your first broadcast.</p>
            </div>
          ) : (
            <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#f0f0f3] text-left font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">
                  <th className="px-4 py-3">Transmission</th><th className="px-4 py-3">Sent to</th><th className="px-4 py-3">Seen</th><th className="px-4 py-3">Sent</th><th className="px-3 py-3 w-20"></th>
                </tr></thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} data-testid="transmission-row" className="border-b border-[#f6f6f9] hover:bg-[#FCFCFE]">
                      <td className="px-4 py-3"><div className="font-medium text-[#06060C]">{t.title || "(untitled)"}</div><div className="text-[11px] text-[#9FA1A4]">by {t.created_by_name}</div></td>
                      <td className="px-4 py-3 text-[#595D62]">{t.recipient_count} user{t.recipient_count !== 1 ? "s" : ""}</td>
                      <td className="px-4 py-3 text-[#595D62]">{t.seen_count}/{t.recipient_count}</td>
                      <td className="px-4 py-3 text-[#595D62]">{fmt(t.created_at)}</td>
                      <td className="px-3 py-3"><div className="flex items-center gap-1.5">
                        <button data-testid={`transmission-view-${t.id}`} onClick={() => setViewing(t)} title="View" className="h-8 w-8 rounded-full border border-[#EEEFEF] flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><Eye className="h-4 w-4" /></button>
                        <button data-testid={`transmission-delete-${t.id}`} onClick={() => del(t.id)} title="Delete" className="h-8 w-8 rounded-full border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-rose-600 hover:border-rose-200"><Trash2 className="h-4 w-4" /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {viewing && <TransmissionModal t={viewing} logoUrl={logoUrl} onClose={() => setViewing(null)} />}
    </main>
  );
}

function TransmissionEditor({ agency, logoUrl, onBack, onSent }) {
  const [title, setTitle] = useState("");
  const [heroAssetId, setHeroAssetId] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);
  const [messageHtml, setMessageHtml] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [ctaLinkType, setCtaLinkType] = useState("url");
  const [ctaLink, setCtaLink] = useState("");
  const [ctaLinkLabel, setCtaLinkLabel] = useState("");
  const [allFlag, setAllFlag] = useState(true);
  const [groups, setGroups] = useState(() => new Set());
  const [userIds, setUserIds] = useState(() => new Set());
  const [users, setUsers] = useState([]);
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const fileRef = React.useRef();

  useEffect(() => { api.get("/agency/users").then(({ data }) => setUsers(data.filter((u) => u.status !== "invited"))); }, []);
  useEffect(() => {
    if (ctaLinkType !== "internal" || q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(() => { api.get(`/console/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults((data.tasks || []).slice(0, 6))).catch(() => {}); }, 250);
    return () => clearTimeout(id);
  }, [q, ctaLinkType]);

  const uploadHero = async (file) => {
    if (!file) return;
    setHeroUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/console/transmissions/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setHeroAssetId(data.asset_id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setHeroUploading(false); }
  };
  const toggleGroup = (k) => setGroups((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleUser = (id) => setUserIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const audienceCount = allFlag ? users.length : users.filter((u) => groups.has(u.role) || userIds.has(u.id)).length;
  const previewT = { title, hero_asset_id: heroAssetId, message_html: messageHtml, cta: ctaText ? { text: ctaText, link: ctaLink, link_type: ctaLinkType } : null };

  const send = async () => {
    if (!title.trim() && !messageHtml.replace(/<[^>]+>/g, "").trim()) { toast.error("Add a title or a message"); return; }
    if (!allFlag && groups.size === 0 && userIds.size === 0) { toast.error("Pick who should receive this"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/console/transmissions", {
        title, hero_asset_id: heroAssetId, message_html: messageHtml,
        cta_text: ctaText, cta_link: ctaLink, cta_link_type: ctaLinkType, cta_link_label: ctaLinkLabel,
        audience: { all: allFlag, groups: [...groups], user_ids: [...userIds] },
      });
      toast.success(`Transmission sent to ${data.sent} user${data.sent !== 1 ? "s" : ""}`);
      onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <div data-testid="transmission-editor">
      <button onClick={onBack} data-testid="transmission-back" className="flex items-center gap-1.5 text-sm text-[#5b2fd6] hover:text-[#723FFF] mb-4"><ArrowLeft className="h-4 w-4" /> All transmissions</button>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-5">
          <h1 className="font-display text-2xl font-semibold text-[#06060C]">New transmission</h1>

          <div>
            <label className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4] mb-1.5 block">Title</label>
            <Input data-testid="transmission-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New Scheduler is live" className="bg-white border-[#EEEFEF]" />
          </div>

          <div>
            <label className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4] mb-1.5 block">Hero image</label>
            <div className="flex items-center gap-3">
              <div className="w-40 rounded-xl overflow-hidden border border-[#f0f0f3] bg-[#F6F6FD] shrink-0" style={{ aspectRatio: "16 / 9" }}>
                {heroAssetId ? <img src={heroUrl(heroAssetId)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#c4c5cc]"><ImagePlus className="h-6 w-6" /></div>}
              </div>
              <div className="space-y-1.5">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadHero(e.target.files?.[0])} />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={heroUploading} className="border-[#EEEFEF] bg-white text-[#595D62]" data-testid="transmission-hero-upload">{heroUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload image"}</Button>
                {heroAssetId && <button onClick={() => setHeroAssetId(null)} className="block text-[11px] text-[#9FA1A4] hover:text-rose-600">Remove</button>}
                <p className="text-[11px] text-[#c4c5cc]">Cropped to a 16:9 banner.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4] mb-1.5 block">Message</label>
            <div className="bg-white rounded-xl border border-[#EEEFEF] overflow-hidden" data-testid="transmission-message">
              <ReactQuill theme="snow" value={messageHtml} onChange={setMessageHtml} modules={QUILL_MODULES} placeholder="Write your message…" />
            </div>
          </div>

          <div className="rounded-2xl border border-[#f0f0f3] bg-[#FCFCFE] p-4 space-y-3">
            <p className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4]">Call to action (optional)</p>
            <Input data-testid="transmission-cta-text" value={ctaText} onChange={(e) => setCtaText(e.target.value)} placeholder="Button text — e.g. Open Scheduler" className="bg-white border-[#EEEFEF]" />
            {ctaText && (
              <>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant={ctaLinkType === "url" ? "default" : "outline"} onClick={() => { setCtaLinkType("url"); setCtaLink(""); }} className={`rounded-full ${ctaLinkType === "url" ? "bg-[#06060C] text-white" : "border-[#EEEFEF] bg-white text-[#595D62]"}`} data-testid="transmission-cta-type-url"><Link2 className="h-3.5 w-3.5 mr-1.5" /> Paste URL</Button>
                  <Button type="button" size="sm" variant={ctaLinkType === "internal" ? "default" : "outline"} onClick={() => { setCtaLinkType("internal"); setCtaLink(""); }} className={`rounded-full ${ctaLinkType === "internal" ? "bg-[#06060C] text-white" : "border-[#EEEFEF] bg-white text-[#595D62]"}`} data-testid="transmission-cta-type-internal"><Search className="h-3.5 w-3.5 mr-1.5" /> Link in platform</Button>
                </div>
                {ctaLinkType === "url" ? (
                  <Input data-testid="transmission-cta-url" value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} placeholder="https://…" className="bg-white border-[#EEEFEF]" />
                ) : (
                  <div className="space-y-2">
                    <Select value="" onValueChange={(v) => { setCtaLink(`/console?nav=${v}`); setCtaLinkLabel(MODULES.find((m) => m[0] === v)?.[1] || ""); }}>
                      <SelectTrigger data-testid="transmission-cta-module" className="bg-white border-[#EEEFEF] h-9"><SelectValue placeholder="Jump to a module…" /></SelectTrigger>
                      <SelectContent className="bg-white border-[#EEEFEF] max-h-60">{MODULES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#c4c5cc]" />
                      <Input data-testid="transmission-cta-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="…or search a task by name/code" className="bg-white border-[#EEEFEF] pl-8" />
                    </div>
                    {results.length > 0 && (
                      <div className="rounded-xl border border-[#EEEFEF] bg-white overflow-hidden">
                        {results.map((r) => (
                          <button key={r.id} type="button" data-testid={`transmission-cta-result-${r.id}`} onClick={() => { setCtaLink(`/console?task=${r.id}`); setCtaLinkLabel(`${r.code} · ${r.title}`); setResults([]); setQ(`${r.code} · ${r.title}`); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F6F6FD] border-b border-[#f7f7f9] last:border-0"><span className="font-mono-data text-[11px] text-[#5b2fd6]">{r.code}</span> {r.title}</button>
                        ))}
                      </div>
                    )}
                    {ctaLink && <p className="text-[11px] text-[#5b2fd6] flex items-center gap-1"><Check className="h-3 w-3" /> Links to: {ctaLinkLabel || ctaLink}</p>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-[#f0f0f3] bg-[#FCFCFE] p-4 space-y-3">
            <p className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4]">Recipients · {audienceCount}</p>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox data-testid="transmission-audience-all" checked={allFlag} onCheckedChange={(v) => setAllFlag(!!v)} className="h-4 w-4 rounded-[5px] border-[#d3d4d8] data-[state=checked]:bg-[#723FFF] data-[state=checked]:border-[#723FFF]" />
              <span className="text-sm text-[#06060C] font-medium">All agency users</span>
            </label>
            <div className={allFlag ? "opacity-40 pointer-events-none" : ""}>
              <p className="text-[11px] text-[#9FA1A4] mb-1.5">Groups</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {GROUPS.map((g) => (
                  <button key={g.key} type="button" data-testid={`transmission-group-${g.key}`} onClick={() => toggleGroup(g.key)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${groups.has(g.key) ? "bg-[#723FFF] text-white" : "bg-white border border-[#EEEFEF] text-[#595D62] hover:bg-[#F6F6FD]"}`}>{g.label}</button>
                ))}
              </div>
              <p className="text-[11px] text-[#9FA1A4] mb-1.5">Individual users</p>
              <div className="max-h-52 overflow-y-auto rounded-xl border border-[#EEEFEF] bg-white divide-y divide-[#f7f7f9]">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[#F6F6FD]">
                    <Checkbox data-testid={`transmission-user-${u.id}`} checked={userIds.has(u.id)} onCheckedChange={() => toggleUser(u.id)} className="h-4 w-4 rounded-[5px] border-[#d3d4d8] data-[state=checked]:bg-[#723FFF] data-[state=checked]:border-[#723FFF]" />
                    <span className="text-sm text-[#06060C]">{u.name}</span><span className="text-[11px] text-[#9FA1A4] ml-auto">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setPreview(true)} className="border-[#EEEFEF] bg-white text-[#595D62] rounded-full flex-1" data-testid="transmission-preview"><Eye className="h-4 w-4 mr-1.5" /> Preview</Button>
            <Button type="button" onClick={send} disabled={sending} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full flex-1" data-testid="transmission-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send</>}</Button>
          </div>
        </div>
      </div>
      {preview && <TransmissionModal t={previewT} logoUrl={logoUrl} onClose={() => setPreview(false)} />}
    </div>
  );
}
